import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AppTheme from './shared-theme/AppTheme';

import Login from './pages/auth/Login';
import SplashScreen from './components/SplashScreen';

import ProtectedRoute from './components/ProtectedRoute';
import routes from './routes';
import {
  dataGridCustomizations,
  datePickersCustomizations,
} from './theme/customizations';
import { subscribeUserToPush } from './util/push-notifications';

const xThemeComponents = {
  ...dataGridCustomizations,
  ...datePickersCustomizations,
};

export default function App(props) {
  const { user } = useAuth();
  useEffect(() => {
    const userId = user?.id;
    if (userId) {
      // subscribeUserToPush(userId);
      if (typeof Notification !== 'undefined') {
        if (Notification.permission === 'default') {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              subscribeUserToPush(userId);
            } else {
              console.warn('Notification permission denied.');
            }
          });
        } else if (Notification.permission === 'granted') {
          subscribeUserToPush(userId);
        }
      } else {
        console.warn('Notification API not supported in this browser.');
      }
    } else {
      console.log('User not logged in, skipping push notification subscription attempt.');
    }
  }, [user]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Router>
        <Suspense fallback={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100vw',
              backgroundColor: 'background.default'
            }}
          >
            <CircularProgress />
          </Box>
        }>
          <Routes>
            {/* Login route */}
            <Route path="/login" element={<Login />} />
            <Route path="/splash" element={<SplashScreen />} />

            {/* Dynamic routing */}
            {routes.map((route, index) => {
              const RouteComponent = route.component;

              if (route.children) {
                return route.children.map((child, childIndex) => {
                  const ChildComponent = child.component;

                  return (
                    <Route
                      key={`child-${childIndex}`}
                      path={child.path}
                      element={
                        child.protected ? (
                          <ProtectedRoute>
                            <ChildComponent />
                          </ProtectedRoute>
                        ) : (
                          <ChildComponent />
                        )
                      }
                    />
                  );
                });
              }

              return (
                <Route
                  key={`route-${index}`}
                  path={route.path}
                  element={
                    route.protected ? (
                      <ProtectedRoute>
                        <RouteComponent />
                      </ProtectedRoute>
                    ) : (
                      <RouteComponent />
                    )
                  }
                />
              );
            })}

            {/* Default routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AppTheme>
  );
}
